/**
 * Site Permissions Admin Page
 *
 * Configure per-site module permission overrides.
 * Permissions are organised into the same accordion categories as the role
 * permissions modal (Safety / Energy & Resources / Sustainability /
 * System & Administration), with Select All / Deselect All per category,
 * counts, and a search box.
 *
 * The underlying permission model remains per-site, per-role, per-module
 * with six action booleans (Read/Write/Amend/Approve/Export/Admin).
 *
 * SOC 2 Controls:
 * - CC6.2: Per-site granular permission management
 * - CC6.7: Default-deny with explicit overrides
 * - CC7.2: Audit trail (mutations logged server-side)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import TabNavigation from '../../components/TabNavigation';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ShieldCheck, RotateCcw, Info, Shield, Zap, Sparkles, Briefcase, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import { usePermissions } from '../../context/PermissionContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ACTIONS = ['can_read', 'can_write', 'can_amend', 'can_approve', 'can_export', 'can_admin'];
// UI labels mirror the role-permissions verbs (Create / Read / Update / Delete / Approve / Export).
// The underlying SiteModulePermission columns remain unchanged:
//   can_write  → "Create"   (back-end column governs create+write)
//   can_amend  → "Update"
//   can_admin  → "Delete"   (back-end column also covers admin/manage operations)
const ACTION_LABELS = {
  can_read: 'Read',
  can_write: 'Create',
  can_amend: 'Update',
  can_approve: 'Approve',
  can_export: 'Export',
  can_admin: 'Delete',
};

/**
 * Module hierarchy mirrors the Role Permissions modal (RoleManagement.jsx).
 * Board Governance is kept as its own top-level category (ERM, Board Process)
 * — it is a distinct business module, not a system/admin function.
 */
const MODULE_HIERARCHY = [
  {
    name: 'Safety',
    Icon: Shield,
    modules: [
      { slug: 'safety', name: 'Safety' },
      { slug: 'hira', name: 'HIRA' },
      { slug: 'ptw', name: 'Permit to Work' },
      { slug: 'capa', name: 'CAPA' },
      { slug: 'incidents', name: 'Incidents' },
      { slug: 'training', name: 'Training' },
      { slug: 'audits', name: 'Audits' },
      { slug: 'meetings', name: 'Meetings' },
      { slug: 'ssem', name: 'SSEM' },
      { slug: 'documents', name: 'Documents' },
    ],
  },
  {
    name: 'Energy & Resources',
    Icon: Zap,
    modules: [
      { slug: 'ers', name: 'ERS Studies' },
      { slug: 'energy', name: 'Energy Tracking' },
    ],
  },
  {
    name: 'Sustainability',
    Icon: Sparkles,
    modules: [
      { slug: 'ssum', name: 'SSUM' },
      { slug: 'cbam', name: 'CBAM' },
      { slug: 'gri', name: 'GRI/CSRD' },
      { slug: 'lca', name: 'LCA' },
      { slug: 'brsr', name: 'BRSR' },
      { slug: 'sgx', name: 'SGX' },
      { slug: 'ecovadis', name: 'EcoVadis' },
    ],
  },
  {
    name: 'Board Governance',
    Icon: Briefcase,
    modules: [
      { slug: 'erm', name: 'ERM' },
      { slug: 'board_process', name: 'Board Process' },
    ],
  },
];

const SitePermissions = ({ user, onLogout, hideNavigation = false }) => {
  const { token } = useAuth();
  const { sites } = useOrg();
  const { isAdmin } = usePermissions();

  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [roles, setRoles] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [defaultPerms, setDefaultPerms] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [backendReady, setBackendReady] = useState(true);
  const [busy, setBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState({});

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Expand all categories by default
  useEffect(() => {
    const init = {};
    MODULE_HIERARCHY.forEach(cat => { init[cat.name] = true; });
    setExpanded(init);
  }, []);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/tenant-admin/roles`, { headers });
      setRoles(res.data.roles || res.data || []);
    } catch (err) {
      console.warn('[SitePermissions] Could not fetch roles:', err.message);
    }
  }, [headers]);

  // Fetch overrides for selected site + role
  const fetchOverrides = useCallback(async () => {
    if (!selectedSiteId || !selectedRoleId) {
      setOverrides([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_BASE}/api/rbac/site-module-permissions?site_id=${selectedSiteId}&role_id=${selectedRoleId}`,
        { headers }
      );
      setOverrides(res.data.permissions || res.data || []);
      setBackendReady(true);
    } catch (err) {
      if (err.response?.status === 404) {
        setBackendReady(false);
        setOverrides([]);
      } else {
        setError(err.response?.data?.detail || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [headers, selectedSiteId, selectedRoleId]);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);
  useEffect(() => { fetchOverrides(); }, [fetchOverrides]);

  // --- Permission state helpers ---

  const getOverride = useCallback(
    (slug) => overrides.find(o => o.module_slug === slug),
    [overrides]
  );

  const isActionEnabled = useCallback((slug, action) => {
    const override = getOverride(slug);
    if (override) return !!override[action];
    return defaultPerms[slug]?.[action] || false;
  }, [getOverride, defaultPerms]);

  const isOverridden = useCallback(
    (slug) => !!getOverride(slug),
    [getOverride]
  );

  // --- Search / counts ---

  const matchesSearch = (text) => {
    if (!searchQuery) return true;
    return (text || '').toLowerCase().includes(searchQuery.toLowerCase());
  };

  const filteredCategories = useMemo(() => {
    return MODULE_HIERARCHY.map(cat => ({
      ...cat,
      modules: cat.modules.filter(m => matchesSearch(m.name) || matchesSearch(cat.name)),
    })).filter(cat => cat.modules.length > 0);
  }, [searchQuery]);

  const getCategoryCount = useCallback((cat) => {
    let selected = 0;
    let total = 0;
    cat.modules.forEach(m => {
      ACTIONS.forEach(a => {
        total += 1;
        if (isActionEnabled(m.slug, a)) selected += 1;
      });
    });
    return { selected, total };
  }, [isActionEnabled]);

  const totalCount = useMemo(() => {
    let selected = 0;
    let total = 0;
    MODULE_HIERARCHY.forEach(cat => {
      cat.modules.forEach(m => {
        ACTIONS.forEach(a => {
          total += 1;
          if (isActionEnabled(m.slug, a)) selected += 1;
        });
      });
    });
    return { selected, total };
  }, [isActionEnabled]);

  // --- Mutations ---

  // Upsert helper: if an override exists, PATCH the supplied actions; else POST a new override.
  // For new overrides, any unspecified action falls back to defaultPerms (or false).
  const upsertOverride = async (slug, actionValues) => {
    const existing = getOverride(slug);
    if (existing) {
      const payload = {};
      ACTIONS.forEach(a => {
        if (actionValues[a] !== undefined) payload[a] = actionValues[a];
      });
      await axios.put(
        `${API_BASE}/api/rbac/site-module-permissions/${existing.id}`,
        payload,
        { headers }
      );
    } else {
      const payload = {
        site_node_id: selectedSiteId,
        role_id: selectedRoleId,
        module_slug: slug,
      };
      ACTIONS.forEach(a => {
        payload[a] = actionValues[a] !== undefined
          ? actionValues[a]
          : (defaultPerms[slug]?.[a] || false);
      });
      await axios.post(`${API_BASE}/api/rbac/site-module-permissions`, payload, { headers });
    }
  };

  const handleToggle = async (slug, action) => {
    if (!backendReady || !selectedSiteId || !selectedRoleId) return;
    setBusy(true);
    try {
      const current = isActionEnabled(slug, action);
      await upsertOverride(slug, { [action]: !current });
      await fetchOverrides();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Select All / Deselect All for a whole category — sets every action on every module
  // in the category to the same target value via per-module upserts.
  const handleCategoryToggleAll = async (cat) => {
    if (!backendReady || !selectedSiteId || !selectedRoleId) return;
    const { selected, total } = getCategoryCount(cat);
    const target = !(selected === total && total > 0);
    setBusy(true);
    try {
      const allActions = {};
      ACTIONS.forEach(a => { allActions[a] = target; });
      await Promise.all(cat.modules.map(m => upsertOverride(m.slug, allActions)));
      await fetchOverrides();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Reset module to default (delete override)
  const handleReset = async (slug) => {
    const override = getOverride(slug);
    if (!override) return;
    setBusy(true);
    try {
      await axios.delete(`${API_BASE}/api/rbac/site-module-permissions/${override.id}`, { headers });
      await fetchOverrides();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleCategory = (name) =>
    setExpanded(prev => ({ ...prev, [name]: !prev[name] }));

  // --- Render ---

  if (!isAdmin()) {
    return (
      <div className="p-8 text-center text-gray-500">
        <p>You do not have permission to manage site permissions.</p>
      </div>
    );
  }

  const hasContext = selectedSiteId && selectedRoleId;

  const content = (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" />
          Site Module Permissions
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Configure per-site permission overrides. Coloured toggles are site-specific overrides; grey toggles reflect default role permissions.
        </p>
      </div>

      {!backendReady && (
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <Info className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            Site module permission endpoints are not yet available (Phase 1 backend required).
            Default role permissions are shown below in read-only mode.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert className="mb-6" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Selectors */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
              <select
                value={selectedSiteId}
                onChange={(e) => setSelectedSiteId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select site...</option>
                {(sites || []).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select role...</option>
                {/* BUG-274 (R2): Only site-scope roles belong in the Site
                    Permissions dropdown. Corporate roles are PA-managed and
                    must not leak into tenant-facing assignment flows. */}
                {roles
                  .filter(r => (r.scope_type || 'site').toLowerCase() === 'site')
                  .map(r => (
                    <option key={r.id || r.name} value={r.id}>
                      {r.display_name || r.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {hasContext ? (
        <Card>
          <CardContent className="pt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : (
              <>
                {/* Search */}
                <input
                  type="text"
                  placeholder="Search permissions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm mb-3"
                />

                <div className="mb-3 text-sm text-gray-600">
                  Select permissions for this role:
                  <span className="ml-2 font-medium">
                    ({totalCount.selected} of {totalCount.total} selected)
                  </span>
                </div>

                {/* Accordion categories */}
                <div className="space-y-2">
                  {filteredCategories.map(cat => {
                    const count = getCategoryCount(cat);
                    const isExpanded = !!expanded[cat.name];
                    const allSelected = count.selected === count.total && count.total > 0;
                    const Icon = cat.Icon;

                    return (
                      <div key={cat.name} className="border rounded-lg overflow-hidden bg-white">
                        {/* Category header */}
                        <div
                          className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                          onClick={() => toggleCategory(cat.name)}
                        >
                          <div className="flex items-center gap-2">
                            <ChevronRight
                              className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            />
                            {Icon && <Icon className="w-5 h-5 text-gray-600" />}
                            <span className="font-semibold text-gray-900">{cat.name}</span>
                            <span className="text-xs text-gray-500">
                              ({count.selected}/{count.total})
                            </span>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCategoryToggleAll(cat); }}
                            disabled={!backendReady || busy}
                            className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                          >
                            {allSelected ? 'Deselect All' : 'Select All'}
                          </button>
                        </div>

                        {/* Module rows with 6 action checkboxes */}
                        {isExpanded && (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50/40 border-b">
                                <tr>
                                  <th className="text-left px-4 py-2 font-medium text-gray-500 w-48">Module</th>
                                  {ACTIONS.map(action => (
                                    <th key={action} className="text-center px-3 py-2 font-medium text-gray-500 w-20">
                                      {ACTION_LABELS[action]}
                                    </th>
                                  ))}
                                  <th className="text-center px-3 py-2 font-medium text-gray-500 w-20">Reset</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {cat.modules.map(mod => {
                                  const hasOverride = isOverridden(mod.slug);
                                  return (
                                    <tr key={mod.slug} className={hasOverride ? 'bg-blue-50/30' : ''}>
                                      <td className="px-4 py-2 font-medium text-gray-800">
                                        {mod.name}
                                        {hasOverride && (
                                          <span className="ml-2 text-xs text-blue-600 font-normal">(override)</span>
                                        )}
                                      </td>
                                      {ACTIONS.map(action => {
                                        const enabled = isActionEnabled(mod.slug, action);
                                        return (
                                          <td key={action} className="text-center px-3 py-2">
                                            <input
                                              type="checkbox"
                                              checked={enabled}
                                              onChange={() => handleToggle(mod.slug, action)}
                                              disabled={!backendReady || busy}
                                              className={`w-4 h-4 rounded cursor-pointer ${
                                                hasOverride ? 'accent-blue-600' : 'accent-gray-400 opacity-60'
                                              }`}
                                            />
                                          </td>
                                        );
                                      })}
                                      <td className="text-center px-3 py-2">
                                        {hasOverride ? (
                                          <button
                                            onClick={() => handleReset(mod.slug)}
                                            className="text-gray-400 hover:text-red-500 p-1"
                                            title="Reset to default"
                                            disabled={busy}
                                          >
                                            <RotateCcw className="w-4 h-4" />
                                          </button>
                                        ) : (
                                          <span className="text-gray-300 text-xs">—</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {filteredCategories.length === 0 && (
                    <div className="text-center text-gray-400 py-8 text-sm">
                      No modules match "{searchQuery}".
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-gray-400">
            Select a site and role to view and configure permissions.
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <div className="mt-4 flex items-center gap-6 text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <input type="checkbox" checked readOnly className="w-3.5 h-3.5 accent-gray-400 opacity-60" />
          <span>Default (from role)</span>
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked readOnly className="w-3.5 h-3.5 accent-blue-600" />
          <span>Site override</span>
        </div>
      </div>
    </div>
  );

  if (hideNavigation) return content;

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      {content}
    </div>
  );
};

export default SitePermissions;
